import React, { useRef, useEffect, useState, useContext } from "react";
import "./PrivacyPolicy.css";
import {
  FaInfoCircle,
  FaDatabase,
  FaList,
  FaCogs,
  FaShareAlt,
  FaUserShield,
  FaLock,
  FaEnvelope,
} from "react-icons/fa";
import { MyContext } from "../Context/MyContext";
import { ContentPageSkeleton } from "../Components/Skeleton/Skeleton";
import PageLoadGate from "../Components/Skeleton/PageLoadGate";

export default function PrivacyPolicy() {
  const { privacy } = useContext(MyContext);
  const [active, setActive] = useState("intro");

  //  Stable refs object (Fix for ESLint warning)
  const refs = useRef({
    intro: React.createRef(),
    collection: React.createRef(),
    types: React.createRef(),
    use: React.createRef(),
    third: React.createRef(),
    rights: React.createRef(),
    security: React.createRef(),
    contact: React.createRef(),
  });

  const scrollTo = (id) => {
    refs.current[id].current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-40% 0px -55% 0px",
        threshold: 0,
      }
    );

    Object.values(refs.current).forEach((r) => {
      if (r.current) observer.observe(r.current);
    });

    return () => observer.disconnect();
  }, []);

  const menu = [
    { id: "intro", label: "Introduction", icon: <FaInfoCircle /> },
    { id: "collection", label: "Data Collection", icon: <FaDatabase /> },
    { id: "types", label: "Types of Data", icon: <FaList /> },
    { id: "use", label: "Use of Data", icon: <FaCogs /> },
    { id: "third", label: "Third-Party", icon: <FaShareAlt /> },
    { id: "rights", label: "User Rights", icon: <FaUserShield /> },
    { id: "security", label: "Security", icon: <FaLock /> },
    { id: "contact", label: "Contact", icon: <FaEnvelope /> },
  ];

  const sections = privacy.sections;

  return (
    <PageLoadGate skeleton={<ContentPageSkeleton />} delay={380}>
    <div className="privacy-page">
      {/* HERO */}
      <div className="privacy-hero">
        <div className="privacy-hero-overlay">
          <h1>{privacy.hero.title}</h1>
          <p>{privacy.hero.subtitle}</p>
        </div>
      </div>

      <div className="privacy-container">
        {/* SIDEBAR */}
        <aside className="privacy-sidebar">
          <div className="toc-title">Table of contents</div>
          <ul>
            {menu.map((item) => (
              <li
                key={item.id}
                className={active === item.id ? "active" : ""}
                onClick={() => scrollTo(item.id)}
              >
                <span className="icon">{item.icon}</span>
                {item.label}
              </li>
            ))}
          </ul>
        </aside>

        {/* CONTENT */}
        <main className="privacy-content">

          {/* INTRO */}
          <section id="intro" ref={refs.current.intro}>
            <h2>{sections.intro.title}</h2>
            {sections.intro.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>

          {/* COLLECTION */}
          <section id="collection" ref={refs.current.collection}>
            <h2>{sections.collection.title}</h2>
            <p>{sections.collection.intro}</p>
            <ul>
              {sections.collection.list.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          {/* TYPES */}
          <section id="types" ref={refs.current.types}>
            <h2>{sections.types.title}</h2>
            <ul>
              {sections.types.list.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          {/* USE */}
          <section id="use" ref={refs.current.use}>
            <h2>{sections.use.title}</h2>
            <ul>
              {sections.use.list.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          {/* THIRD */}
          <section id="third" ref={refs.current.third}>
            <h2>{sections.third.title}</h2>
            <p>{sections.third.intro}</p>
            <ul>
              {sections.third.list.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p>{sections.third.note}</p>
          </section>

          {/* RIGHTS */}
          <section id="rights" ref={refs.current.rights}>
            <h2>{sections.rights.title}</h2>
            <ul>
              {sections.rights.list.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          {/* SECURITY */}
          <section id="security" ref={refs.current.security}>
            <h2>{sections.security.title}</h2>
            <ul>
              {sections.security.list.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p>{sections.security.note}</p>
          </section>

          {/* CONTACT */}
          <section id="contact" ref={refs.current.contact}>
            <h2>{sections.contact.title}</h2>
            <p>{sections.contact.intro}</p>
            <p><b>Email:</b> {sections.contact.email}</p>
            <p><b>Phone:</b> {sections.contact.phone}</p>
            <p><b>Address:</b> {sections.contact.address}</p>
          </section>

        </main>
      </div>
    </div>
    </PageLoadGate>
  );
}